/**
 * API client для бэкенда.
 * Токен из localStorage (записан в telegramBootstrap.ts после auth redirect).
 * Отправляется как Authorization: Bearer <token>. См. ADR-010.
 */

import { clearStoredAuth, getStoredToken } from '../auth/telegramBootstrap';

const API_BASE = import.meta.env.DEV ? '/api' : '/api';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getStoredToken();
  if (!token) {
    throw new ApiError(401, 'Нет токена. Войдите через Telegram.');
  }

  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (response.status === 401) {
    // Токен протух или отозван — стираем, форсим повторный логин.
    clearStoredAuth();
    window.location.reload();
    throw new ApiError(401, 'Сессия истекла. Войдите снова.');
  }

  if (!response.ok) {
    // 413 от nginx — body не JSON, дать понятное сообщение.
    if (response.status === 413) {
      throw new ApiError(
        413,
        'Запись слишком длинная (>40 минут). Попробуйте короче или несколько отдельных сессий.',
      );
    }
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      detail = body.detail || detail;
    } catch {
      // ignore
    }
    throw new ApiError(response.status, detail);
  }

  return response.json();
}

export interface Specialty {
  slug: string;
  name: string;
}

export interface Section {
  slug: string;
  name: string;
}

export interface StatsResponse {
  period: 'day' | 'week' | 'month' | 'all';
  asr_rate_per_minute_rub: number;
  baseline_minutes_per_protocol: number;
  with_tool_minutes_per_protocol: number;
  totals: {
    transcribe_sessions: number;
    structure_sessions: number;
    asr_seconds: number;
    asr_minutes: number;
    asr_cost_rub: number;
    time_saved_minutes: number;
  };
  by_user: Array<{
    user_name: string;
    telegram_id: number;
    transcribe_sessions: number;
    structure_sessions: number;
    asr_minutes: number;
    asr_cost_rub: number;
    last_active: string | null;
  }>;
  by_day: Array<{
    day: string;
    transcribe_sessions: number;
    asr_minutes: number;
    asr_cost_rub: number;
  }>;
}

export const api = {
  async me(): Promise<{ name: string; telegram_id: number; is_admin: boolean }> {
    return request('/me');
  },

  async adminStats(period: 'day' | 'week' | 'month' | 'all'): Promise<StatsResponse> {
    return request(`/admin/stats?period=${period}`);
  },

  async specialties(): Promise<{ specialties: Specialty[]; default: string }> {
    return request('/specialties');
  },

  async sections(): Promise<{ sections: Section[]; default: string }> {
    return request('/sections');
  },

  async transcribe(
    audioBlob: Blob,
    specialty?: string,
  ): Promise<{ text: string; duration_ms: number }> {
    const formData = new FormData();
    // Расширение по MIME — backend сам определит формат по magic bytes,
    // но корректное имя полезно для логов и debugging.
    const ext = audioBlob.type.includes('mp4') ? 'mp4'
      : audioBlob.type.includes('ogg') ? 'ogg'
      : 'webm';
    formData.append('audio', audioBlob, `recording.${ext}`);
    if (specialty) formData.append('specialty', specialty);
    return request('/transcribe', { method: 'POST', body: formData });
  },

  async structure(
    text: string,
    specialty?: string,
    section?: string,
  ): Promise<{
    structured: string;
    prompt_version: string;
    duration_ms: number;
    author_can_receive: boolean;
  }> {
    return request('/structure', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, specialty, section }),
    });
  },

  async downloadPdf(
    variant: 'full' | 'patient',
    payload: {
      complaints?: string;
      history?: string;
      examination?: string;
      diagnosis?: string;
      recommendations?: string;
      specialty?: string;
    },
  ): Promise<Blob> {
    const token = getStoredToken();
    if (!token) throw new ApiError(401, 'Нет токена.');
    const response = await fetch(`${API_BASE}/pdf/${variant}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (response.status === 401) {
      clearStoredAuth();
      window.location.reload();
      throw new ApiError(401, 'Сессия истекла.');
    }
    if (!response.ok) {
      throw new ApiError(response.status, `Не удалось сгенерировать PDF (${response.status})`);
    }
    return response.blob();
  },

  async structureProtocol(
    text: string,
    specialty?: string,
  ): Promise<{
    complaints: string;
    history: string;
    examination: string;
    diagnosis: string;
    recommendations: string;
    duration_ms: number;
    author_can_receive: boolean;
  }> {
    return request('/structure-protocol', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, specialty }),
    });
  },
};

export { ApiError };
